<template>
  <v2-modal
    ref="modal"
    :show="visible"
    size="full"
    body-scroll
    @close="onShellClose"
  >
    <v2-product-detail-content
      v-if="mode === 'view'"
      :key="`view-${currentProductId}-${refreshKey}`"
      presentation="modal"
      :product-id="currentProductId"
      :collection-id="currentCollectionId"
      :wishlist-id="wishlistId"
      @request-edit="mode = 'edit'"
      @deleted="onDeleted"
      @request-close="closeModal"
      @open-product="onOpenRelatedProduct"
    />
    <v2-product-edit-content
      v-else
      ref="editContent"
      presentation="modal"
      :product-id="currentProductId"
      :collection-id="currentCollectionId"
      :wishlist-id="wishlistId"
      @edit-success="onEditSuccess"
      @request-close="onEditClose"
    />
  </v2-modal>
</template>

<script>
import V2Modal from '@/components/v2/V2Modal.vue'
import V2ProductDetailContent from '@/views/v2/product/V2ProductDetailContent.vue'
import V2ProductEditContent from '@/views/v2/product/edit/V2ProductEditContent.vue'

export default {
  name: 'V2ProductDetailModal',
  components: {
    V2Modal,
    V2ProductDetailContent,
    V2ProductEditContent,
  },
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    productId: {
      type: String,
      required: true,
    },
    collectionId: {
      type: String,
      required: true,
    },
    wishlistId: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      mode: 'view',
      refreshKey: 0,
      sentinelOnStack: false,
      // KP-3382: the active product shown in the popup. Starts from the props
      // (the product the parent opened) but can change as the user clicks
      // recommended products — without closing/reopening the modal.
      currentProductId: this.productId,
      currentCollectionId: this.collectionId,
    }
  },
  watch: {
    visible(now, prev) {
      if (now && !prev) this.openSentinel()
      if (!now && prev) this.closeSentinel()
      if (now) this.mode = 'view'
    },
    productId() {
      // Parent opened a different product — reset the active product.
      this.currentProductId = this.productId
      this.currentCollectionId = this.collectionId
      this.mode = 'view'
    },
  },
  mounted() {
    if (this.visible) this.openSentinel()
  },
  beforeDestroy() {
    window.removeEventListener('popstate', this.handlePopstate)
    // do NOT call history.back() here — route change consumed it.
    this.sentinelOnStack = false
  },
  methods: {
    openSentinel() {
      window.history.pushState({ kpModal: 'product-detail' }, '', window.location.href)
      this.sentinelOnStack = true
      window.addEventListener('popstate', this.handlePopstate)
    },
    closeSentinel() {
      window.removeEventListener('popstate', this.handlePopstate)
      if (this.sentinelOnStack) {
        window.history.back()
        this.sentinelOnStack = false
      }
    },
    handlePopstate() {
      // sentinel just consumed by browser back
      this.sentinelOnStack = false
      this.onBrowserBack()
    },
    hasUnsavedEditChanges() {
      if (this.mode !== 'edit') return false
      const editContent = this.$refs.editContent
      if (!editContent) return false
      return editContent.isDirty
    },
    onBrowserBack() {
      if (!this.hasUnsavedEditChanges()) {
        this.$emit('close')
        return
      }
      // re-push sentinel so the user stays "in modal" while we ask
      this.sentinelOnStack = true
      window.history.pushState({ kpModal: 'product-detail' }, '', window.location.href)
      window.addEventListener('popstate', this.handlePopstate)
      this.$refs.editContent.requestUnsavedConfirm()
    },
    closeModal() {
      this.closeSentinel()
      this.$emit('close')
    },
    onShellClose() {
      this.closeModal()
    },
    // KP-3382: a recommended product was clicked inside the popup. Swap the
    // active product — the keyed detail content re-mounts with the new product
    // and its own recommendations, all within the same popup.
    onOpenRelatedProduct(product) {
      if (!product || !product._id) return
      this.currentProductId = product._id
      this.currentCollectionId = product.collectionId || this.currentCollectionId
      this.mode = 'view'
      // The user usually scrolls down to the recommended row before clicking;
      // reset the popup scroll so the new product's skeleton/content renders
      // from the top instead of mid-page at the prior scroll offset.
      this.$nextTick(() => {
        if (this.$refs.modal) this.$refs.modal.scrollToTop()
      })
    },
    onDeleted(payload) {
      this.closeSentinel()
      this.$emit('deleted', payload)
      this.$emit('close')
    },
    onEditSuccess() {
      this.refreshKey += 1
      this.mode = 'view'
      this.$emit('edit-success')
    },
    onEditClose() {
      // request-close from edit content fires only after confirmed-discard
      this.closeModal()
    },
  },
}
</script>
